<template>
    <div class="formulaire-creation-preavis">
        <div class="field-group objet-group">
            <TextInput v-model="objet" :label="$t('preavisIntervention.objet.libelle')" @blur="touchedObjet = true" />
            <ErrorMessageFormulaire
                v-if="touchedObjet && !objetOk"
                :message="$t('preavisIntervention.objet.required')"
            />
        </div>

        <div class="field-group delai-group">
            <DropDown
                :display="displayDelai"
                :label="$t('preavisIntervention.delai')"
                :list="delaiOptions"
                :selectedItem="selectedDelaiItem"
                @select="onSelectDelai"
            />
            <ReglePastille cle="preavisIntervention.delaiMax" />
        </div>

        <div class="field-group emails-section">
            <div class="email-input">
                <TextInput
                    v-model="emailDraft"
                    :label="$t('preavisIntervention.emailsSuivi.libelle')"
                    style="width: 100%"
                    @blur="touchedEmail = true"
                    @keyup.enter="addEmail"
                />
                <button :disabled="!canAddEmail" class="btn-add" type="button" @click="addEmail">+</button>
                <ReglePastille cle="preavisIntervention.emailsRegles" />
            </div>
            <ErrorMessageFormulaire
                v-if="touchedEmail && isDuplicateEmail"
                :message="$t('preavisIntervention.emailsSuivi.duplicate')"
            />
            <ErrorMessageFormulaire
                v-if="touchedEmail && emailDraft.trim().length > 0 && !isValidEmail(emailDraft) && !isDuplicateEmail"
                :message="$t('preavisIntervention.emailsSuivi.invalid')"
            />

            <ErrorMessageFormulaire
                v-if="touchedEmail && emails.length >= loadMaxMailPreavis"
                :message="$t('preavisIntervention.emailsSuivi.max')"
            />
            <ul v-if="emails.length > 0" class="email-list">
                <li v-for="(mail, idx) in emails" :key="mail + idx" class="email-item">
                    <span class="email-text">{{ mail }}</span>
                    <img
                        v-if="mail !== lockedEmail"
                        :alt="`${$t('bouton.supprimer')} ${mail}`"
                        :aria-label="`${$t('bouton.supprimer')} ${mail}`"
                        :src="loadIconDelete"
                        class="email-remove"
                        @click="removeEmail(idx)"
                    />
                </li>
            </ul>
            <p v-else class="email-hint">{{ $t('preavisIntervention.mail.empty') }}</p>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import TextInput from '@/components/ui-components/TextInput.vue';
import DropDown from '@/components/ui-components/DropDown.vue';
import ErrorMessageFormulaire from '@/components/ui-components/ErrorMessageFormulaire.vue';
import { MAX_DELAI_PREAVIS, MAX_MAIL_PREAVIS } from '@/constantes/constantes';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'PreavisFormCreateEdit',
    components: { TextInput, DropDown, ErrorMessageFormulaire, ReglePastille },
    props: {
        initialObjet: { type: String, default: '' },
        initialDelai: { type: Number, default: 1 },
        initialEmails: { type: Array as () => string[], default: () => [] },
        mode: { type: String as () => 'create' | 'edit', default: 'create' },
    },
    emits: ['state-change'],
    data() {
        return {
            objet: this.initialObjet as string,
            delai: this.initialDelai as number,
            delaiOptions: Array.from({ length: MAX_DELAI_PREAVIS }, (_, i) => ({
                value: i + 1,
                label: `${i + 1} ${this.$tc('preavisIntervention.jours', i + 1)}`,
            })) as { value: number; label: string }[],
            emailDraft: '' as string,
            emails: (this.mode === 'edit' ? [...this.initialEmails].map(e => e.toLowerCase()) : []) as string[],
            lockedEmail: '' as string,
            touchedObjet: false as boolean,
            touchedEmail: false as boolean,
        };
    },
    computed: {
        loadMaxMailPreavis() {
            return MAX_MAIL_PREAVIS;
        },
        loadIconDelete() {
            return require('@/assets/img/icons/bin.png');
        },
        objetOk(): boolean {
            return this.objet.trim().length > 0;
        },
        canAddEmail(): boolean {
            return (
                this.emailDraft.trim().length > 0 &&
                this.emails.length < MAX_MAIL_PREAVIS &&
                this.isValidEmail(this.emailDraft) &&
                !this.isDuplicateEmail
            );
        },
        isDuplicateEmail(): boolean {
            const draft = this.emailDraft.trim().toLowerCase();
            return draft.length > 0 && this.emails.some(e => e.toLowerCase() === draft);
        },
        selectedDelaiItem(): { value: number; label: string } | undefined {
            return this.delaiOptions.find(o => o.value === this.delai);
        },
        isFormValid(): boolean {
            const delaiOk = Number.isInteger(this.delai) && this.delai >= 1 && this.delai <= MAX_DELAI_PREAVIS;
            const objetOk = this.objet.trim().length > 0;
            const emailsOk =
                this.emails.length > 0 &&
                this.emails.length <= MAX_MAIL_PREAVIS &&
                this.emails.every(this.isValidEmail);
            return delaiOk && objetOk && emailsOk;
        },
    },
    watch: {
        objet() {
            this.emitState();
        },
        delai() {
            this.emitState();
        },
        emails: {
            handler() {
                this.emitState();
            },
            deep: true,
        },
    },
    mounted() {
            // 1. Essayer de récupérer l'email via le store Vuex
            let userEmail = this.$store.getters['authenticationState/getUserEmail']?.toLowerCase();

            // 2. Si non trouvé dans le store, chercher directement dans le localStorage
            if (!userEmail) {
                const localStorageEmail = localStorage.getItem('email');
                if (localStorageEmail) {
                    userEmail = localStorageEmail.replace(/"/g, '').toLowerCase();
                }
            }

            // 3. Si un email a été trouvé, l'ajouter à la liste
            if (userEmail) {
                this.lockedEmail = userEmail;
                if (!this.emails.some(e => e.toLowerCase() === userEmail)) {
                    this.emails.unshift(userEmail);
                }
            }
            this.emitState();
        },
    methods: {
        displayDelai(item: { value: number; label: string }): string {
            return item.label;
        },
        onSelectDelai(item: { value: number; label: string }) {
            this.delai = item.value;
        },
        isValidEmail(email: string): boolean {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email.trim());
        },
        addEmail() {
            if (!this.canAddEmail) return;
            const email = this.emailDraft.trim().toLowerCase();
            if (!this.emails.includes(email)) {
                this.emails.push(email);
            }
            this.emailDraft = '';
        },
        removeEmail(index: number) {
            if (this.emails[index] === this.lockedEmail) return;
            this.emails.splice(index, 1);
        },
        validate() {
            this.touchedObjet = true;
            this.touchedEmail = true;
        },
        emitState() {
            this.$emit('state-change', {
                isFormValid: this.isFormValid,
                objet: this.objet,
                delai: this.delai,
                actif: true,
                emails: this.emails,
            });
        },
    },
});
</script>

<style scoped>
.formulaire-creation-preavis {
    background-color: var(--white);
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    padding: 36px 36px;
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.field-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.emails-section {
    width: 50%;
    gap: 10px;
}

.email-input {
    display: flex;
    gap: 8px;
}

.email-list {
    list-style: none;
    width: 100%;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #dde2ea;
    border-radius: 6px;
    overflow: hidden;
}

.email-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid #eef1f5;
    background-color: #f5f7fa;
}

.email-item:last-child {
    border-bottom: none;
}

.email-text {
    font-size: 13px;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.email-remove {
    background: none;
    border: none;
    color: #999;
    cursor: pointer;
    font-size: 14px;
    padding: 0 0 0 8px;
    line-height: 1;
    display: flex;
    align-items: center;
    transition: color 0.15s;
    flex-shrink: 0;
}

.email-remove:hover {
    color: #e53935;
}

.email-hint {
    margin: 0;
    font-size: 12px;
    color: #999;
    font-style: italic;
}

.btn-add {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 6px;
    border: none;
    background-color: var(--color-principal);
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
    margin-bottom: 2px;
}

.btn-add:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.objet-group {
    max-width: 60%;
}

.delai-group {
    position: relative;
    max-width: 200px;
}
</style>
